import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { isValidMgrs, mgrsCenter, mgrsFromLatLon } from './mgrs';

const COORDINATE_PATTERN = /^-?\d+([.,]\d+)?$/;

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === '';
}

/**
 * A latitude or longitude typed into a field (or already a number) as a number; NaN if it is not a plain decimal
 * number. A comma is accepted as the decimal separator.
 */
export function parseCoordinate(value: string | number | null | undefined): number {
  if (typeof value === 'number') {
    return value;
  }
  if (isBlank(value) || !COORDINATE_PATTERN.test(String(value).trim())) {
    return NaN;
  }
  return Number(String(value).trim().replace(',', '.'));
}

/** A given MGRS has to be one that can be plotted; a blank one is left to the required validator. */
export function mgrsValidator(control: AbstractControl): ValidationErrors | null {
  return !isBlank(control.value) && !isValidMgrs(control.value) ? { mgrs: true } : null;
}

function round6(degrees: number): number {
  return Math.round(degrees * 1_000_000) / 1_000_000;
}

/**
 * Makes the position follow the MGRS in a form that has `latitude`, `longitude` and `mgrs` controls: a valid MGRS
 * sets the latitude and longitude to the center of its square (what an SRE report does). They are set without
 * events. An MGRS that is not filled in yet is filled in from the position.
 *
 * @returns the subscriptions to unsubscribe from when the form is no longer shown
 */
export function linkLocationControls(form: FormGroup): Subscription[] {
  const latitude = form.get('latitude');
  const longitude = form.get('longitude');
  const mgrs = form.get('mgrs');
  if (!latitude || !longitude || !mgrs) {
    return [];
  }

  const setMgrsFromPosition = () => {
    const reference = mgrsFromLatLon(parseCoordinate(latitude.value), parseCoordinate(longitude.value));
    if (reference) {
      mgrs.setValue(reference, { emitEvent: false });
    }
  };

  if (isBlank(mgrs.value)) {
    setMgrsFromPosition();
  }

  return [
    mgrs.valueChanges.subscribe((value) => {
      const center = mgrsCenter(value);
      if (center) {
        latitude.setValue(round6(center.latitude), { emitEvent: false });
        longitude.setValue(round6(center.longitude), { emitEvent: false });
      }
    }),
  ];
}
